import { createObservablePart } from '../utils/create-observable-part.function.js';
import { deepFreeze } from '../utils/deep-freeze.function.js';
import type { MappingFunction } from '../types/mapping-function.type.js';
import type { MemoizationFunction } from '../types/memoization-function.type.js';
import { jsonStringComparison } from '../utils/json-string-comparison.function.js';
import { UmbBasicState } from './basic-state.js';
import type { Observable } from '@umbraco-cms/backoffice/external/rxjs';

/**
 * @class UmbDeepState
 * @augments {UmbBasicState<T>}
 * @template T
 * @description - A RxJS BehaviorSubject which deepFreezes the data to ensure its not manipulated from any implementations.
 * Additionally the Subject ensures the data is unique, not updating any Observes unless there is an actual change of the content.
 */
export class UmbDeepState<T> extends UmbBasicState<T> {
	#mute?: boolean;
	#value: T;
	#muteResolvers?: Array<(value: boolean) => void>;

	constructor(initialData: T) {
		super(deepFreeze(initialData));
		this.#value = this._subject.getValue();
	}

	/**
	 * @function createObservablePart
	 * @param {(mappable: T) => ReturnType} mappingFunction - Method to return the part for this Observable to return.
	 * @param {(previousResult: ReturnType, currentResult: ReturnType) => boolean} [memoizationFunction] - Method to compare two results. Should return true when data is the same (unchanged), preventing unnecessary emissions.
	 * @returns {Observable<ReturnType>} The derived Observable.
	 * @description - Creates an Observable from this State.
	 */
	override asObservablePart<ReturnType>(
		mappingFunction: MappingFunction<T, ReturnType>,
		memoizationFunction?: MemoizationFunction<ReturnType>,
	): Observable<ReturnType> {
		return createObservablePart(this._subject, mappingFunction, memoizationFunction ?? jsonStringComparison);
	}

	/**
	 * @function setValue
	 * @param {T} data - The next data for this state to hold.
	 * @description - Set the data of this state, if data is different than current this will trigger observations to update.
	 */
	override setValue(data: T): void {
		if (!this._subject) throw new Error('_subject is undefined');
		const frozenData = deepFreeze(data);
		this.#value = frozenData;
		// Only update data if its not muted and is different than current data. [NL]
		if (!this.#mute && !jsonStringComparison(frozenData, this._subject.getValue())) {
			this._subject.next(frozenData);
		}
	}

	override getValue(): T {
		return this.#value;
	}

	/**
	 * @function mute
	 * @description - Set mute for this state.
	 */
	mute() {
		if (this.#mute) return;
		this.#mute = true;
	}

	/**
	 * @function unmute
	 * @description - Unset the mute of this state.
	 */
	unmute() {
		if (!this.#mute) return;
		this.#mute = false;
		// Only update data if it is different than current data. [NL]
		if (!this._subject) throw new Error('_subject is undefined');
		if (!jsonStringComparison(this.#value, this._subject.getValue())) {
			this._subject.next(this.#value);
		}
		// Resolve any pending mute promises — independent of whether an emission occurred. [NL]
		if ((this.#muteResolvers?.length ?? 0) > 0) {
			const resolvers = this.#muteResolvers!;
			this.#muteResolvers = [];
			resolvers.forEach((resolve) => resolve(true));
		}
	}

	/**
	 * @function isMuted
	 * @description - Check if the state is muted.
	 * @returns {boolean} - Returns true if the state is muted.
	 */
	isMuted(): boolean {
		return this.#mute ?? false;
	}

	/**
	 * @function getMutePromise
	 * @description - Get a promise which resolves when the mute is unset.
	 * @returns {Promise<boolean>} - Returns a promise which resolves to true if the state was muted and is now unmuted, or false if the state was not muted.
	 */
	getMutePromise(): Promise<boolean> {
		if (!this.#mute) {
			return Promise.resolve(false);
		}
		return new Promise<boolean>((resolve) => {
			(this.#muteResolvers ??= []).push(resolve);
		});
	}

	override destroy(): void {
		// Drain any pending mute promises so awaiters don't hang. [NL]
		if ((this.#muteResolvers?.length ?? 0) > 0) {
			const resolvers = this.#muteResolvers!;
			this.#muteResolvers = [];
			resolvers.forEach((resolve) => resolve(false));
		}
		super.destroy();
	}
}
